import React, { useState, useRef, useEffect } from "react"
import fuzzysort from "fuzzysort"
import { createPopper } from "@popperjs/core"
import classNames from "classnames"
import { Editor, Range } from "@tiptap/core"

import { useNana } from "../../../../../hooks/useNana"
import { Idea } from "../../../../../../model/types"
import Model from "../../../../../../model/model"
import IdeaBox from "../../../../../components/idea_box"
import { replaceRangeWithPageLinkToIdea } from "./page_link"
import { MosaicPageState } from "../../../mosaic"

const updateIdeas = (model: Model, filter: string): Idea[] => {
  return fuzzysort
    .go(filter, model.getIdeasWithTitles(), {
      limit: 10,
      threshold: -1000,
      keys: ["title", "memo"],
    })
    .map((result) => result.obj)
}

export default function PageLinkPopup({
  suggestionHooks,
  editor,
}: {
  suggestionHooks: any
  editor: Editor
}) {
  const { model } = useNana()
  const popupElementRef = useRef<HTMLDivElement>(null)

  const [targetElement, setTargetElement] = useState<HTMLElement | undefined>(
    undefined
  )
  const [ideas, setIdeas] = useState<Idea[]>(updateIdeas(model, ""))
  const [selected, setSelected] = useState<number>(0)
  const [range, setRange] = useState<Range | undefined>(undefined)
  const [query, setQuery] = useState<string>("")

  const selectItem = (index: number) => {
    if (!range) return

    replaceRangeWithPageLinkToIdea(editor, range, ideas[index])
    setTargetElement(undefined)
  }

  // The Suggestion hooks need access to this state, so pass it in.
  useEffect(() => {
    suggestionHooks.onStart = (props: any) => {
      setTargetElement(props.decorationNode)
      updateIdeas(model, props.query)
      setIdeas(ideas)
      setSelected(0)
      MosaicPageState.escapeShouldCloseEditor = false
    }

    suggestionHooks.onUpdate = (props: any) => {
      setQuery(props.query)
      setTargetElement(props.decorationNode)
      setRange(props.range)
      setIdeas(updateIdeas(model, props.query))
    }

    suggestionHooks.onKeyDown = (props: any) => {
      switch (props.event.key) {
        case "ArrowUp":
          setSelected(Math.max(0, selected - 1))
          return true
        case "ArrowDown":
          setSelected(Math.min(4, selected + 1))
          return true
        case "Enter":
          selectItem(selected)
          return true
        default:
          return false
      }
    }

    suggestionHooks.onExit = () => {
      setTargetElement(undefined)
      MosaicPageState.escapeShouldCloseEditor = true
    }
  }, [
    ideas,
    selected,
    range,
    selectItem,
    setTargetElement,
    updateIdeas,
    setIdeas,
    setSelected,
    setRange,
    model,
  ])

  useEffect(() => {
    if (!targetElement) return
    if (popupElementRef.current == null) return

    createPopper(targetElement, popupElementRef.current, {
      placement: "bottom-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 5],
          },
        },
      ],
    })
  }, [popupElementRef, targetElement])

  if (!targetElement) return null

  return (
    <div
      ref={popupElementRef}
      className=" z-50 flex items-center bg-white dark:bg-gray-900 border border-gray-200 shadow-md rounded text-sm"
    >
      {ideas.length === 0 && (
        <div className="px-4 py-2 text-gray-500">
          {query ? "No results found" : "Type to search"}
        </div>
      )}
      <ul className="flex flex-col py-2 items-stretch w-96">
        {ideas.map((idea, i) => (
          <li
            key={idea.id}
            onClick={() => selectItem(i)}
            onMouseEnter={() => {
              setSelected(i)
            }}
            className={classNames(
              "px-4 py-2 flex items-center gap-2  cursor-pointer",
              {
                "bg-gray-100 dark:bg-gray-800": i === selected,
              }
            )}
          >
            <IdeaBox idea={idea} shadow={false} />
            <div className="overflow-x-hidden truncate">{idea.title}</div>
          </li>
        ))}
      </ul>
    </div>
  )
}
